import type { ApiResult } from '../types';
import type {
  CriminalAppellateDocumentRow,
  CriminalAppellateWizardDraft,
} from '../types/criminalAppellateCaseRequest';
import type { DocumentChecklistItem } from '../mocks/criminalMasterData';
import { DEFAULT_CRIMINAL_APPELLATE_DRAFT } from '../layouts/CriminalAppellateRequestLayout';
import { caseRequestIdForSave, applyDraftCurrentStep, parseCurrentStep, strOrNull } from '../utils/caseRequestPayload';
import { caseApiRequest, caseApiPdfRequest } from './apiClient';
import {
  caseRequestStepsPath,
  type CaseRequestViewOptions,
} from './caseRequestViewOptions';
import * as criminalService from './criminalCaseRequests';
import { normalizeCriminalCoverNoteResponse } from './criminalCaseRequests';
import type { CriminalCoverNoteResponse, CriminalSaveStep4 } from '../types/criminalApi';
import { cachedCriminalFetch } from './criminalLookupCache';

export const getProvinces = criminalService.getProvinces;
export const getDistricts = criminalService.getDistricts;
export const getPoliceStations = criminalService.getPoliceStations;
export const getUrgencyLevels = criminalService.getUrgencyLevels;
export const getDeliveryMethods = criminalService.getDeliveryMethods;
export const getCorporateInstitutes = criminalService.getCorporateInstitutes;

/** Re-export the shared multipart upload (CMS-CRC-3); do not duplicate. */
export const uploadCaseFile = criminalService.uploadCaseFile;

type AppellateWizardStep = 1 | 2 | 3 | 4 | 5;

export type SaveCriminalAppellateResult = {
  requestId: string;
  fileReferenceNumber?: string;
};

function boolToYesNo(v: boolean | null | undefined): '' | 'yes' | 'no' {
  if (v === true) return 'yes';
  if (v === false) return 'no';
  return '';
}

function recordField(raw: Record<string, unknown>, key: string): string {
  const v = raw[key];
  return typeof v === 'string' ? v.trim() : '';
}

function nestedRecord(raw: Record<string, unknown>, key: string): Record<string, unknown> | undefined {
  const v = raw[key];
  return v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : undefined;
}

function requestIdFromResponse(data: Record<string, unknown>, fallback: string): string {
  for (const key of ['case_request_id', 'caseRequestId', 'requestId', 'request_id', 'id']) {
    const v = data[key];
    if (typeof v === 'string' && v.trim()) return v.trim();
  }
  return fallback;
}

function referenceFromResponse(data: Record<string, unknown>): string | undefined {
  for (const key of ['reference_no', 'referenceNo', 'file_reference_number', 'fileReferenceNumber']) {
    const v = data[key];
    if (typeof v === 'string' && v.trim()) return v.trim();
  }
  return undefined;
}

function buildCriminalAppellatePayload(
  draft: CriminalAppellateWizardDraft,
  options: {
    isDraft: boolean;
    currentStep?: AppellateWizardStep;
    confirmedCorrect?: boolean;
    isUpdateAlreadySubmitted?: boolean;
  },
): Record<string, unknown> {
  const isCorporation = draft.instituteType === 'corporation';
  const body: Record<string, unknown> = {
    is_draft_status: options.isDraft,
    is_corporation: isCorporation,
    applicable_areas: draft.applicableAreaIds.filter(Boolean),
    // Always send these keys (null when not a corporation) so the backend clears
    // any previously saved institute data when the user changes the selection.
    corporate_institute: isCorporation ? strOrNull(draft.corporateInstitute) : null,
    other_institute: isCorporation ? strOrNull(draft.corporateInstituteOther) : null,
  };
  const caseRequestId = caseRequestIdForSave(draft.draftRequestId);
  if (caseRequestId) body.case_request_id = caseRequestId;

  if (options.confirmedCorrect !== undefined) {
    body.confirmed_correct = options.confirmedCorrect;
  }
  if (options.isUpdateAlreadySubmitted) {
    body.is_update_already_submitted = true;
  }

  const step = options.currentStep;

  if (!step || step >= 1) {
    const judgementDocs = draft.judgementOrderFileName.trim()
      ? [
          {
            file_name: draft.judgementOrderFileName.trim(),
            original_file_name: strOrNull(draft.judgementOrderOriginalFileName),
          },
        ]
      : [];

    body.step_1 = {
      sub_category_id: strOrNull(draft.subFileCategoryId),
      description: strOrNull(draft.description),
      original_court: strOrNull(draft.originalCourt),
      province_id: strOrNull(draft.provinceId),
      district_id: strOrNull(draft.districtId),
      police_station_id: strOrNull(draft.policeStationId),
      is_appeal_matter: draft.appealMatter === 'yes',
      judgment_or_order_documents: judgementDocs,
      external_case_reference_number: strOrNull(draft.yourReferenceNumber),
      other_external_case_reference_number: strOrNull(draft.otherExternalReferences),
      case_number: strOrNull(draft.caseNumber),
      has_connected_case: draft.hasConnectedMatters === 'yes',
      connected_case_number: strOrNull(draft.connectedCaseNumber),
      connected_agd_file_ref_available:
        draft.hasConnectedMatters === 'yes' ? draft.connectedAgdFileRefAvailable : undefined,
      connected_agd_file_ref_number:
        draft.hasConnectedMatters === 'yes' && draft.connectedAgdFileRefAvailable
          ? strOrNull(draft.connectedAgdFileRef)
          : undefined,
    };
  }

  if (!step || step >= 2) {
    body.step_2 = {
      plaintiff_applicant_name: strOrNull(draft.plaintiffApplicantName),
      defendant_respondant_name: strOrNull(draft.defendantRespondentName),
      is_incorporated_body: draft.clientType === 'corporation',
      other_involved_institutions: strOrNull(draft.otherInvolvedInstitutions),
      leagel_office_or_officer_incharge_name: strOrNull(draft.legalOfficerName),
      leagel_office_or_officer_incharge_contact_no: strOrNull(draft.legalOfficerContact),
    };
  }

  if (!step || step >= 3) {
    body.step_3 = {
      urgency_level_id: strOrNull(draft.urgencyLevelId),
      deadline: strOrNull(draft.deadline),
      urgency_justification: strOrNull(draft.urgencyJustification),
    };
  }

  if (!step || step >= 4) {
    body.step_4 = {
      physical_file_delivery_method_ids: draft.deliveryMethodIds.filter(Boolean),
      other_method: strOrNull(draft.deliveryOther),
    };
  }

  if (!step || step >= 5) {
    body.step_5 = {
      uploaded_files: draft.documents
        .filter((doc) => doc.fileName.trim() || doc.skipReason.trim())
        .map((doc) => ({
          document_type_id: strOrNull(doc.documentTypeId),
          file_name: strOrNull(doc.fileName),
          original_file_name: strOrNull(doc.originalFileName),
          reason: strOrNull(doc.skipReason),
        })),
    };
  }

  applyDraftCurrentStep(body, options);

  return body;
}

function mapServerResponseToDraft(raw: unknown): Partial<CriminalAppellateWizardDraft> {
  if (!raw || typeof raw !== 'object') return {};
  const r = raw as Record<string, unknown>;
  const s1 = nestedRecord(r, 'step_1');
  const s2 = nestedRecord(r, 'step_2');
  const s3 = nestedRecord(r, 'step_3');
  const s4 = nestedRecord(r, 'step_4');
  const s5 = nestedRecord(r, 'step_5');

  const applicableAreaIds: string[] = [];
  const applicableAreas: string[] = [];
  const areasRaw = r.applicable_areas;
  if (Array.isArray(areasRaw)) {
    for (const item of areasRaw) {
      if (!item || typeof item !== 'object') continue;
      const row = item as Record<string, unknown>;
      const id = recordField(row, 'applicable_area_id');
      const name = recordField(row, 'applicable_area_name');
      if (id) applicableAreaIds.push(id);
      if (name) applicableAreas.push(name);
    }
  }

  let judgementOrderFileName = '';
  let judgementOrderOriginalFileName = '';
  const judgementRaw = s1?.judgement_or_order_documents;
  if (Array.isArray(judgementRaw) && judgementRaw.length > 0) {
    const first = judgementRaw[0];
    if (first && typeof first === 'object') {
      const doc = first as Record<string, unknown>;
      judgementOrderFileName = recordField(doc, 'file_name');
      judgementOrderOriginalFileName = recordField(doc, 'original_file_name');
    }
  }

  const deliveryMethodIds: string[] = [];
  const deliveryMethods: string[] = [];
  const methodIdsRaw = s4?.physical_file_delivery_method_ids;
  if (Array.isArray(methodIdsRaw)) {
    for (const id of methodIdsRaw) {
      if (typeof id === 'string' && id.trim()) deliveryMethodIds.push(id.trim());
    }
  }
  const methodsRaw = s4?.physical_file_delivery_methods;
  if (Array.isArray(methodsRaw)) {
    for (const item of methodsRaw) {
      if (!item || typeof item !== 'object') continue;
      const row = item as Record<string, unknown>;
      const name = recordField(row, 'delivery_method_name');
      if (name) deliveryMethods.push(name);
    }
  }

  const documents: CriminalAppellateDocumentRow[] = [];
  const uploadedRaw = s5?.uploaded_files;
  if (Array.isArray(uploadedRaw)) {
    for (const item of uploadedRaw) {
      if (!item || typeof item !== 'object') continue;
      const file = item as Record<string, unknown>;
      documents.push({
        id: crypto.randomUUID(),
        checklistItemId: '',
        documentTypeId: recordField(file, 'document_type_id'),
        documentName: '',
        mandatory: false,
        fileName: recordField(file, 'file_name'),
        originalFileName: recordField(file, 'original_file_name'),
        uploadTime: '',
        skipReason: recordField(file, 'reason'),
      });
    }
  }

  const isCorporation = r.is_corporation === true;

  return {
    draftRequestId: recordField(r, 'case_request_id'),
    referenceNo: recordField(r, 'reference_no'),
    fileCategoryId: s1 ? recordField(s1, 'category_id') : '',
    fileCategory: s1 ? recordField(s1, 'category_name') : '',
    subFileCategoryId: s1 ? recordField(s1, 'sub_category_id') : '',
    subFileCategoryName: s1 ? recordField(s1, 'sub_category_name') : '',
    applicableAreaIds,
    applicableAreas,
    instituteType: isCorporation ? 'corporation' : 'government',
    corporateInstitute: recordField(r, 'corporate_institute_id'),
    corporateInstituteName: recordField(r, 'corporate_institute_name'),
    corporateInstituteOther: recordField(r, 'other_institute'),
    description: s1 ? recordField(s1, 'description') : '',
    appealMatter: s1 ? boolToYesNo(s1.is_appeal_matter as boolean | null | undefined) : '',
    originalCourt: s1 ? recordField(s1, 'original_court') : '',
    judgementOrderFileName,
    judgementOrderOriginalFileName,
    provinceId: s1 ? recordField(s1, 'province_id') : '',
    provinceName: s1 ? recordField(s1, 'province_name') : '',
    districtId: s1 ? recordField(s1, 'district_id') : '',
    districtName: s1 ? recordField(s1, 'district_name') : '',
    policeStationId: s1 ? recordField(s1, 'police_station_id') : '',
    policeStationName: s1 ? recordField(s1, 'police_station_name') : '',
    yourReferenceNumber: s1 ? recordField(s1, 'external_case_reference_number') : '',
    otherExternalReferences: s1 ? recordField(s1, 'other_external_case_reference_number') : '',
    caseNumber: s1 ? recordField(s1, 'case_number') : '',
    hasConnectedMatters: s1 ? boolToYesNo(s1.has_connected_case as boolean | null | undefined) : '',
    connectedCaseNumber: s1 ? recordField(s1, 'connected_case_number') : '',
    connectedAgdFileRefAvailable: (() => {
      if (s1?.connected_agd_file_ref_available != null) {
        return Boolean(s1.connected_agd_file_ref_available);
      }
      const ref = s1 ? recordField(s1, 'connected_agd_file_ref_number') : '';
      return Boolean(ref);
    })(),
    connectedAgdFileRef: s1 ? recordField(s1, 'connected_agd_file_ref_number') : '',
    plaintiffApplicantName: s2 ? recordField(s2, 'plaintiff_applicant_name') : '',
    defendantRespondentName: s2 ? recordField(s2, 'defendant_respondant_name') : '',
    clientType: s2?.is_incorporated_body === true ? 'corporation' : 'government',
    otherInvolvedInstitutions: s2 ? recordField(s2, 'other_involved_instruction') : '',
    legalOfficerName: s2 ? recordField(s2, 'leagel_office_or_officer_incharge_name') : '',
    legalOfficerContact: s2 ? recordField(s2, 'leagel_office_or_officer_incharge_contact_no') : '',
    urgencyLevelId: s3 ? recordField(s3, 'urgency_level_id') : '',
    urgencyLevelName: s3 ? recordField(s3, 'urgency_level_name') : '',
    deadline: s3 ? recordField(s3, 'deadline') : '',
    urgencyJustification: s3 ? recordField(s3, 'urgency_justification') : '',
    deliveryMethodIds,
    deliveryMethods,
    deliveryOther: s4 ? recordField(s4, 'other_method') : '',
    documents,
  };
}

export async function saveCriminalAppellateRequest(
  draft: CriminalAppellateWizardDraft,
  options: {
    isDraft: boolean;
    currentStep?: AppellateWizardStep;
    confirmedCorrect?: boolean;
    isUpdateAlreadySubmitted?: boolean;
  },
): Promise<ApiResult<SaveCriminalAppellateResult>> {
  const body = buildCriminalAppellatePayload(draft, {
    isDraft: options.isDraft,
    currentStep: options.currentStep,
    confirmedCorrect: options.confirmedCorrect,
    isUpdateAlreadySubmitted: options.isUpdateAlreadySubmitted,
  });

  const requestId = draft.draftRequestId.trim();
  const res = await caseApiRequest<Record<string, unknown>>('/criminal-appellate/steps/save', {
    method: 'POST',
    body,
  });

  if (!res.ok) return { success: false, message: res.message };

  const data = res.data && typeof res.data === 'object' ? res.data : {};
  return {
    success: true,
    data: {
      requestId: requestIdFromResponse(data, requestId),
      fileReferenceNumber: referenceFromResponse(data),
    },
  };
}

export async function getDocumentChecklist(
  fileCategoryId: string,
  subCategoryId?: string,
): Promise<ApiResult<DocumentChecklistItem[]>> {
  if (!fileCategoryId) return { success: true, data: [] };
  const subId = subCategoryId?.trim() ?? '';
  const cacheKey = subId
    ? `appellate-document-checklist:${fileCategoryId}:${subId}`
    : `appellate-document-checklist:${fileCategoryId}`;
  return cachedCriminalFetch(cacheKey, () =>
    criminalService.getDocumentChecklist(fileCategoryId, subCategoryId),
  );
}

export type CriminalAppellateCoverSubmitter = {
  agency: string;
  division: string;
};

export type CriminalAppellateRequestViewData = {
  draft: CriminalAppellateWizardDraft;
  statusName: string;
  submittedAt: string;
  completionRemarks?: string;
  step4?: CriminalSaveStep4;
  coverSubmitter?: CriminalAppellateCoverSubmitter;
};

/** CMS-CRAP-1 — full GET payload for View Case Request / continue draft */
export async function getCriminalAppellateRequestViewData(
  caseRequestId: string,
  options?: CaseRequestViewOptions,
): Promise<ApiResult<CriminalAppellateRequestViewData | null>> {
  const id = caseRequestId.trim();
  if (!id) return { success: true, data: null };

  const res = await caseApiRequest<Record<string, unknown>>(
    caseRequestStepsPath(`/criminal-appellate/steps/${encodeURIComponent(id)}`, options),
    {
      ...(options?.signal ? { signal: options.signal } : {}),
    },
  );
  if (!res.ok) return { success: false, message: res.message };

  const partial = mapServerResponseToDraft(res.data);
  const draft: CriminalAppellateWizardDraft = {
    ...DEFAULT_CRIMINAL_APPELLATE_DRAFT,
    ...partial,
    draftRequestId: requestIdFromResponse(res.data, id),
  };

  const coverRes = await getCoverNote(id);
  const submittedAt =
    (coverRes.success ? coverRes.data.requestSubmittedAt : '').trim() ||
    recordField(res.data, 'created_at') ||
    recordField(res.data, 'updated_at');

  const statusName = recordField(res.data, 'status_name') || recordField(res.data, 'status_code');

  const coverSubmitter =
    coverRes.success &&
    (coverRes.data.submittedAgency.trim() || coverRes.data.submittedDivisionName.trim())
      ? {
          agency: coverRes.data.submittedAgency.trim(),
          division: coverRes.data.submittedDivisionName.trim(),
        }
      : undefined;

  const raw = res.data as { completion_remarks?: string };
  const step4 = nestedRecord(res.data, 'step_4') as CriminalSaveStep4 | undefined;

  return {
    success: true,
    data: {
      draft,
      statusName,
      submittedAt,
      completionRemarks: typeof raw.completion_remarks === 'string' ? raw.completion_remarks : undefined,
      step4,
      coverSubmitter,
    },
  };
}


export async function getCriminalAppellateRequestDetails(
  requestId: string,
): Promise<ApiResult<CriminalAppellateWizardDraft | null>> {
  const view = await getCriminalAppellateRequestViewData(requestId);
  if (!view.success) return { success: false, message: view.message };
  return { success: true, data: view.data?.draft ?? null };
}

function criminalAppellateStep1LooksComplete(s1: Record<string, unknown> | undefined): boolean {
  return Boolean(
    recordField(s1 ?? {}, 'sub_category_id') &&
      recordField(s1 ?? {}, 'province_id') &&
      recordField(s1 ?? {}, 'external_case_reference_number'),
  );
}

function criminalAppellateStep2LooksComplete(s2: Record<string, unknown> | undefined): boolean {
  return Boolean(
    recordField(s2 ?? {}, 'plaintiff_applicant_name') &&
      recordField(s2 ?? {}, 'defendant_respondant_name'),
  );
}

function criminalAppellateStep3LooksComplete(s3: Record<string, unknown> | undefined): boolean {
  return Boolean(recordField(s3 ?? {}, 'urgency_level_id'));
}

function criminalAppellateStep4LooksComplete(s4: Record<string, unknown> | undefined): boolean {
  const methodIdsRaw = s4?.physical_file_delivery_method_ids;
  return Array.isArray(methodIdsRaw) && methodIdsRaw.some((id) => typeof id === 'string' && id.trim());
}

function criminalAppellateStep5LooksComplete(s5: Record<string, unknown> | undefined): boolean {
  const uploadedRaw = s5?.uploaded_files;
  if (!Array.isArray(uploadedRaw)) return false;
  return uploadedRaw.some((item) => {
    if (!item || typeof item !== 'object') return false;
    const file = item as Record<string, unknown>;
    return (
      Boolean(recordField(file, 'file_name') || recordField(file, 'original_file_name')) ||
      Boolean(recordField(file, 'reason'))
    );
  });
}

function resolveCriminalAppellateDraftResumeStep(d: Record<string, unknown>): AppellateWizardStep {
  const apiStep = parseCurrentStep(d.current_step);
  if (apiStep !== undefined && apiStep >= 1 && apiStep <= 5) return apiStep as AppellateWizardStep;

  const s1 = nestedRecord(d, 'step_1');
  const s2 = nestedRecord(d, 'step_2');
  const s3 = nestedRecord(d, 'step_3');
  const s4 = nestedRecord(d, 'step_4');
  const s5 = nestedRecord(d, 'step_5');

  if (!criminalAppellateStep1LooksComplete(s1)) return 1;
  if (!criminalAppellateStep2LooksComplete(s2)) return 2;
  if (!criminalAppellateStep3LooksComplete(s3)) return 3;
  if (!criminalAppellateStep4LooksComplete(s4)) return 4;
  if (!criminalAppellateStep5LooksComplete(s5)) return 5;
  return 5;
}

export async function getCriminalAppellateDraftResumeStep(
  requestId: string,
): Promise<ApiResult<{ step: AppellateWizardStep; requestId: string }>> {
  if (!requestId.trim()) return { success: false, message: 'Case request id is missing.' };
  const res = await caseApiRequest<Record<string, unknown>>(
    `/criminal-appellate/steps/${encodeURIComponent(requestId.trim())}`,
  );
  if (!res.ok) return { success: false, message: res.message };

  const d = res.data && typeof res.data === 'object' ? (res.data as Record<string, unknown>) : {};
  const id = requestIdFromResponse(d, requestId.trim());

  return { success: true, data: { step: resolveCriminalAppellateDraftResumeStep(d), requestId: id } };
}

export async function getCoverNote(requestId: string): Promise<ApiResult<CriminalCoverNoteResponse>> {
  const id = requestId.trim();
  if (!id) return { success: false, message: 'Case request id is required.' };
  const res = await caseApiRequest<unknown>(`/cover-note/${encodeURIComponent(id)}`);
  if (!res.ok) return { success: false, message: res.message };
  const normalized = normalizeCriminalCoverNoteResponse(res.data);
  if (!normalized) return { success: false, message: 'Cover note response is invalid.' };
  return { success: true, data: normalized };
}

export async function downloadCoverNotePdf(requestId: string): Promise<ApiResult<Blob>> {
  const id = requestId.trim();
  if (!id) return { success: false, message: 'Case request id is required.' };
  const res = await caseApiPdfRequest(`/cover-note/pdf/${encodeURIComponent(id)}`);
  if (!res.ok) return { success: false, message: res.message };
  return { success: true, data: res.data };
}
